// helpers/AssignmentHelpers.js

import { DataStore } from "@aws-amplify/datastore";
import { Submissions, EventsSourceType } from "../models"; // adjust path to match your project structure
import { raiseEvent } from "./EventHelpers";

export const validateSubmissionIds = (submissionIds) => {
  if (!submissionIds || !Array.isArray(submissionIds) || submissionIds.length < 1) {
    throw new Error("submissionIds is not initialized or not an array or is empty");
  }
};

export const validateReviewerIds = (reviewerIds) => {
  if (!reviewerIds || !Array.isArray(reviewerIds) || reviewerIds.length < 1) {
    throw new Error("reviewerIds is not initialized or not an array or is empty");
  }
};

export const findReviewerById = (reviewers, reviewerId) => {
  const reviewer = reviewers.find((user) => user.id === reviewerId);
  if (!reviewer) {
    console.error(`Reviewer with ID ${reviewerId} not found.`);
  }
  return reviewer;
};

export const findSubmissionById = (submissions, submissionId) => {
  const submission = submissions.find((sub) => sub.id === submissionId);
  if (!submission) {
    console.error(`Submission with ID ${submissionId} not found.`);
  }
  return submission;
};

export const prepareSubmissionCopy = (submission) => {
  const submissionCopy = JSON.parse(JSON.stringify(submission));

  if (submissionCopy.meta) {
    submissionCopy.meta.creator =
      typeof submissionCopy.meta.creator === "string"
        ? JSON.parse(submissionCopy.meta.creator)
        : submissionCopy.meta.creator;
    submissionCopy.meta.owner =
      typeof submissionCopy.meta.owner === "string"
        ? JSON.parse(submissionCopy.meta.owner)
        : submissionCopy.meta.owner;
  }

  return submissionCopy;
};

export const createNewSubmission = (
  submissionCopy,
  reviewer,
  reviewingFormToken,
  currentProject
) => {
  return new Submissions({
    meta: {
      page: 0,
      owner: JSON.stringify({
        familyName: reviewer.familyName,
        givenName: reviewer.givenName,
        userId: reviewer.id,
        email: reviewer.email,
      }),
      creator: JSON.stringify({
        familyName: reviewer.familyName,
        givenName: reviewer.givenName,
        userId: reviewer.id,
        email: reviewer.email,
      }),
      team: [],
    },
    owner: reviewer.id,
    data: { submission: submissionCopy },
    formToken: reviewingFormToken,
    token: "",
    projectId: currentProject.id,
    projectToken: currentProject.token,
    userId: reviewer.id,
  });
};

export const callProcessAssignmentsLambda = async (url, submissionIds) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ submissionIds }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  // Parse the JSON body of the response
  const responseBody = await response.json();

  // Now extract updatedSubmissions
  const updatedSubmissionsData = responseBody.updatedSubmissions;
  return updatedSubmissionsData;
};

export const updateSubmissionsInDataStore = async (updatedSubmissions, savedSubmissions) => {
  await Promise.all(
    updatedSubmissions.map(updatedSubmission =>
      DataStore.save(
        Submissions.copyOf(savedSubmissions.find(sub => sub.id === updatedSubmission.id), (updatedSub) => {
          updatedSub.meta.trackingNumber = updatedSubmission.meta.trackingNumber;
          updatedSub.owner = updatedSubmission.owner;
        })
      )
    )
  );
};

export const updateReviewsState = async (reviewFormTokens, setReviews) => {
  const allSubmissions = await DataStore.query(Submissions); //N.B. Why are we getting them from DataStore and not state?  Is it because subscriptions are not working and we have created the new reviews via a lambda?

  // Filter submissions to include those whose formToken is in the array of reviewFormTokens
  const filteredSubmissions = allSubmissions.filter(sub => reviewFormTokens.includes(sub.formToken));

  setReviews(filteredSubmissions);
  console.log("Reviews state updated:", filteredSubmissions);
};

export const buildNewSubmissions = (reviewerIds, submissionIds, reviewers, submissions, reviewingFormToken, currentProject) => {
  const newSubmissions = [];

  for (const reviewerId of reviewerIds) {
    const reviewer = findReviewerById(reviewers, reviewerId);
    if (!reviewer) continue;

    for (const submissionId of submissionIds) {
      const submission = findSubmissionById(submissions, submissionId);
      if (!submission) continue;

      const submissionCopy = prepareSubmissionCopy(submission);
      const newSubmission = createNewSubmission(
        submissionCopy,
        reviewer,
        reviewingFormToken,
        currentProject
      );
      newSubmissions.push(newSubmission);
    }
  }

  return newSubmissions;
};

/**
 * Builds the assignment payload for reviewers and submissions, including exceptions for existing reviews.
 *
 * @param {string[]} reviewerIds - Array of reviewer GUIDs.
 * @param {string[]} submissionIds - Array of submission GUIDs.
 * @param {Object[]} reviews - Array of existing review objects.
 * @param {string} formToken - Token for the form.
 * @param {string} projectToken - Token for the project.
 * @param {string} accountToken - Token for the account.
 * @param {string} creatorGuid - GUID of the creator.
 * @param {string} templateName - The name of the template that will generate the review object JSON file.
 * @returns {Object} The constructed payload object.
 * @throws {Error} If input variables are not properly set.
 */
export const buildAssignmentPayload = (
  reviewerIds,
  submissionIds,
  reviews,
  formToken,
  projectToken,
  accountToken,
  creatorGuid,
  templateName,
  templateFormToken,
  isTicker,
  isSuperAdmin,
    setDescription,
    description,
    modelType,
    category,
    aiAssignmentApprovedByAdmin = false,
) => {
  // Validate inputs
  if (
    !Array.isArray(reviewerIds) ||
    !Array.isArray(submissionIds) ||
    !Array.isArray(reviews) ||
    typeof formToken !== 'string' ||
    typeof projectToken !== 'string' ||
    typeof accountToken !== 'string' ||
    typeof creatorGuid !== 'string' ||
    typeof templateName !== 'string' ||
    typeof templateFormToken !== 'string' ||
    typeof isTicker !== 'boolean' ||
    typeof isSuperAdmin !== 'boolean' ||
    typeof setDescription !== 'string' ||
    typeof description !== 'string' ||
    typeof modelType !== 'string' ||
    typeof category !== 'string' ||
    typeof aiAssignmentApprovedByAdmin !== 'boolean'
  ) {
    throw new Error("Invalid input parameters.");
  }

  // Preprocess reviews into a Map for efficient lookup
  const reviewMap = new Map();

  for (const review of reviews) {
    const reviewerId = review.userId;
    const submissionId = review.data?.submission?.id;

    if (reviewerId && submissionId) {
      if (!reviewMap.has(reviewerId)) {
        reviewMap.set(reviewerId, new Set());
      }
      reviewMap.get(reviewerId).add(submissionId);
    }
  }

  // Initialize exceptions object
  const exceptions = {};

  // Populate exceptions only for reviewers with duplicates
  for (const reviewerId of reviewerIds) {
    const reviewedSubmissions = reviewMap.get(reviewerId);
    if (!reviewedSubmissions) continue;

    const duplicates = [];
    for (const submissionId of submissionIds) {
      if (reviewedSubmissions.has(submissionId)) {
        duplicates.push(submissionId);
      }
    }

    if (duplicates.length > 0) {
      exceptions[reviewerId] = duplicates;
    }
  }

  // Determine if all assignments are duplicates
  const totalPossibleAssignments = reviewerIds.length * submissionIds.length;
  let totalDuplicates = 0;
  for (const reviewerId in exceptions) {
    totalDuplicates += exceptions[reviewerId].length;
  }

  // If all possible assignments are duplicates, return a message
  if (totalDuplicates === totalPossibleAssignments) {
    return { message: "No new assignments to create (duplicates not allowed)." };
  }

  // Otherwise, construct and return the payload
  const payload = {
    submissionGuids: submissionIds,
    userGuids: reviewerIds,
    creatorGuid,
    formToken,
    projectToken,
    accountToken,
    exceptions,
    templateName,
    templateFormToken,
    setDescription,
    description,
    modelType,
    category,
    aiAssignmentApprovedByAdmin,
    ozLog: {isTicker, isSuperAdmin},
  };

  return { payload };
};

export const buildAndAssignSubmissions = (
  reviewerIds,
  submissionIds,
  reviewers,
  submissions,
  reviews,
  reviewingFormToken,
  currentProject
) => {
  const newSubmissions = [];

  for (const reviewerId of reviewerIds) {
    const reviewer = reviewers.find((user) => user.id === reviewerId);
    if (!reviewer) {
      throw new Error(`Reviewer with ID ${reviewerId} not found.`);
    }

    for (const submissionId of submissionIds) {
      // Check if the pair already exists as a review
      const exists = doesReviewExist(reviews, submissionId, reviewerId);
      if (exists) {
        console.log(`HNU: Skipping creation for existing review submission: ${submissionId}, reviewer: ${reviewerId}`);
        continue;
      }

      const submission = submissions.find((sub) => sub.id === submissionId);
      if (!submission) {
        throw new Error(`Submission with ID ${submissionId} not found.`);
      }

      // Perform a deep copy of the submission object
      const submissionCopy = structuredClone(submission);

      // Ensure meta fields are properly parsed if needed
      if (submissionCopy.meta) {
        submissionCopy.meta.creator =
          typeof submissionCopy.meta.creator === "string"
            ? JSON.parse(submissionCopy.meta.creator)
            : submissionCopy.meta.creator;
        submissionCopy.meta.owner =
          typeof submissionCopy.meta.owner === "string"
            ? JSON.parse(submissionCopy.meta.owner)
            : submissionCopy.meta.owner;
      }

      // Ensure files field is kept as an object
      if (submissionCopy.files && typeof submissionCopy.files === "string") {
        submissionCopy.files = JSON.parse(submissionCopy.files);
      }

      //Create a new object the stringify way - works but we have to consider the impact
      const newSubmission = new Submissions({
        meta: {
          page: 0,
          owner: JSON.stringify({
            familyName: reviewer.familyName,
            givenName: reviewer.givenName,
            userId: reviewer.id,
            email: reviewer.email,
          }),
          creator: JSON.stringify({
            familyName: reviewer.familyName,
            givenName: reviewer.givenName,
            userId: reviewer.id,
            email: reviewer.email,
          }),
          team: [],
        },
        owner: reviewer.id,
        data: { submission: submissionCopy },
        formToken: reviewingFormToken,
        token: "",
        projectId: currentProject.id,
        projectToken: currentProject.token,
        userId: reviewer.id,

      });

      newSubmissions.push(newSubmission);
    }
  }

  return newSubmissions;
};

export const doesReviewExist = (reviews, submissionId, reviewerId) => {
//  console.log("reviews", reviews);
//  console.log("submissionId", submissionId);
//  console.log("reviewerId", reviewerId);

  if (
    !submissionId || !reviewerId ||
    !reviews || !Array.isArray(reviews) ) {
      throw new Error("Variables are not set");
    }
  return reviews.some(review =>
    review.data.submission.id === submissionId && review.userId === reviewerId
  );
};

/**
 * Updates the 'hidden' attribute on multiple submissions in DataStore.
 *
 * @param {string[]} submissionIds - IDs of submissions to update.
 * @param {boolean} hiddenValue - Value to set for 'hidden' (true = hide, false = unhide).
 * @param {Submissions[]} savedSubmissions - Current list of synced submissions from DataStore.
 */
export const setSubmissionsHiddenStatus = async (submissionIds = [], hiddenValue, savedSubmissions = []) => {
  if (!Array.isArray(submissionIds) || submissionIds.length === 0) return;

  const idSet = new Set(submissionIds);
  const toUpdate = savedSubmissions.filter(sub => idSet.has(sub.id));

  await Promise.all(
    toUpdate.map(original =>
      DataStore.save(
        Submissions.copyOf(original, updated => {
          updated.hidden = hiddenValue;
        })
      )
    )
  );
};

export const raiseAssignmentsEvent = async ({
  reviewerIds,
  submissionIds,
  reviews,
  formToken,
  projectToken,
  accountToken,
  creatorGuid,
  templateName,
  templateFormToken,
  isTicker,
  isSuperAdmin,
  modelType,
  category,
  description,
  setDescription,
  aiAssignmentApprovedByAdmin = false,
}) => {
  if (!Array.isArray(reviewerIds) || reviewerIds.length === 0) {
    throw new Error("raiseAssignmentsEvent: reviewerIds must be a non-empty array");
  }
  if (!Array.isArray(submissionIds) || submissionIds.length === 0) {
    throw new Error("raiseAssignmentsEvent: submissionIds must be a non-empty array");
  }
  if (!Array.isArray(reviews)) {
    throw new Error("raiseAssignmentsEvent: reviews must be an array");
  }
  if (!formToken || !projectToken || !accountToken || !creatorGuid || 
    !templateName || !templateFormToken || description === undefined || 
    modelType === undefined || category === undefined || setDescription === undefined) {
    throw new Error("raiseAssignmentsEvent: required parameters missing");
  } 

  const result = buildAssignmentPayload(
    reviewerIds,
    submissionIds,
    reviews,
    formToken,
    projectToken,
    accountToken,
    creatorGuid,
    templateName,
    templateFormToken,
    isTicker,
    isSuperAdmin,
    setDescription,
    description,
    modelType,
    category,
    aiAssignmentApprovedByAdmin
  );

  console.log("raiseAssignmentsEvent: result =", result);

  if (result?.message) {
    return { ok: true, skipped: true, message: result.message };
  }

  const payload = result?.payload;
  if (!payload) {
    throw new Error("raiseAssignmentsEvent: buildAssignmentPayload returned no payload");
  }

  await raiseEvent({
    event: "SubmissionsAssigned",
    sourceId: `${EventsSourceType.USER}:${creatorGuid}`,
    sourceType: EventsSourceType.USER,
    userId: creatorGuid,
    projectToken,
    payload,
  });

  return {
    ok: true,
    skipped: false,
    message:
      "Your reviews are being created (subject to duplication). Please navigate to the Reviews view to see your new assignments.",
  };
};
